import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import useCurrentUser from '../../hooks/useCurrentUser';
import { request } from "../../utils/api";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import { stateMandateComplianceSchema } from "./application.validation";
import ApplicationFormFooter from './ApplicationFormFooter';
import {
  pickRecordFromListResult,
  parseRecordId,
} from "../../utils/applicationFormRecord";

const StateMandateCompliance = ({ onClose }) => {
  const [complianceId, setComplianceId] = useState(null);

  const { tenantId: defaultTenantId, userId: defaultUserId, companyName, fullName, seniorStaffNurse } = useCurrentUser();

  const methods = useForm({
    resolver: zodResolver(stateMandateComplianceSchema),
    defaultValues: {
      tenantId: resolveFormTenantId(defaultTenantId),
      userId: defaultUserId ?? undefined,
      applicantName: "",
      applicantSignature: "",
      applicantSignedBy: "",
      applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      applicantStatementAccepted: false,
      clinicianName: fullName || "",
    },
    mode: "onChange"
  });

  const { handleSubmit, watch, reset } = methods;

  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ["applicantSignedAt", "reviewedBySignedAt"],
        { flag: actualFlag }
      );

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      const recordId = parseRecordId(complianceId, actualFromId);

      if (recordId) {
        payload.complianceId = recordId;
      }

      let response;
      if (recordId) {
        response = await request(`/state-mandate-compliances/${recordId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/state-mandate-compliances', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        const saved = response.result?.compliance;
        if (saved?.id) {
          setComplianceId(saved.id);
        }
        toast.success(response.message || "State Mandate Compliance processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,
    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "applicantName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  useEffect(() => {
    const fromUrl = parseRecordId(actualFromId);
    if (fromUrl) {
      setComplianceId(fromUrl);
    }
  }, [actualFromId]);

  useEffect(() => {
    const fetchStateMandate = async () => {
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !actualFromId) return;

      const effectiveUserId = targetUserId || currentUserData.id;
      const recordIdFromUrl = parseRecordId(actualFromId);

      const applyRecord = (data) => {
        if (!data?.id) return;
        setComplianceId(data.id);
        setCurrentStatus(data.status || "");
        const userFullName = data?.user?.firstName
          ? `${data.user.firstName || ""} ${data.user.lastName || ""}`.trim()
          : "";
        reset({
          ...data,
          applicantName:
            data.applicantName || data.clinicianName || userFullName || "",
          clinicianName: data.clinicianName || userFullName || "",
          ...getStandardizedSignatures(data, isAdmin),
          applicantSignedAt: parseIsoDateToMDYUS(
            getStandardizedSignatures(data, isAdmin).applicantSignedAt ||
              getLocalSystemDate(),
          ),
          reviewedBySignedAt: parseIsoDateToMDYUS(
            getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || "",
          ),
          applicantStatementAccepted: data.applicantStatementAccepted || false,
        });
      };

      try {
        let data = null;

        if (recordIdFromUrl) {
          try {
            const byIdResponse = isAdmin
              ? await request(
                  `/state-mandate-compliances/${recordIdFromUrl}?userId=${effectiveUserId}`,
                )
              : await request(
                  `/state-mandate-compliances/${recordIdFromUrl}`,
                );
            if (byIdResponse.status === 1) {
              data = pickRecordFromListResult(
                byIdResponse.result,
                effectiveUserId,
              );
            }
          } catch (byIdError) {
            console.error("Error fetching state mandate by id:", byIdError);
          }
        }

        if (!data && effectiveUserId) {
          const listResponse = await request(
            `/state-mandate-compliances?userId=${effectiveUserId}&limit=10`,
          );
          if (listResponse.status === 1) {
            data = pickRecordFromListResult(
              listResponse.result,
              effectiveUserId,
            );
          }
        }

        if (data) {
          applyRecord(data);
        } else if (!isAdmin && currentUserData) {
          const userFullName =
            `${currentUserData.firstName || ""} ${currentUserData.lastName || ""}`.trim();
          reset({
            tenantId: resolveFormTenantId(currentUserData?.tenantId),
            userId: currentUserData.id,
            applicantName: userFullName,
            clinicianName: userFullName,
            applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
          });
        }
      } catch (error) {
        console.error("Error fetching state mandate compliance:", error);
      }
    };

    fetchStateMandate();
  }, [targetUserId, reset, currentUserData, isAdmin, actualFromId]);

  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader
          title="State Mandate Compliance"
          subtitle="PATIENT RECORDS"
        />
        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className={`border-none p-0 m-0 transition-all duration-300 ${isReadOnly ? 'opacity-60 bg-gray-50/50 grayscale-[0.2]' : ''}`}>
                <div className="text-sm leading-relaxed space-y-4 mb-6 text-gray-800">
                  <p>
                    Title 22 of California Code of Regulations, Division 5, Chapter
                    6, is the California Law that governs all HOME HEALTH AGENCIES.
                    Section 74735, Patient Health Records Paragraph (5) states
                    "CLINICAL NOTES SHALL BE SIGNED AND INCORPORATED INTO THE
                    PATIENT'S HEALTH RECORD AT LEAST EVERY SEVEN (7) WORKING DAYS".
                  </p>

                  <p>
                    All <strong>{companyName}</strong> clinicians must comply with this
                    State Mandate. The Agency will require all clinicians to submit
                    the patient visit notes within five (5) days of the visit date,
                    and no later than 5:00 P.M. on Monday.{" "}
                    <span className="underline">There will be no exceptions.</span>
                  </p>

                  <p>
                    <strong>Note:</strong> Notes that are incomplete, missing
                    signature(s), or need clarification will be returned to the
                    clinician for corrections. Therefore, clinicians must submit
                    their notes in a timely manner to allow for review, corrections,
                    final completion and filing into patient's charts, so that both
                    the clinicians and the Agency remain compliant with the STATE
                    REGULATIONS.
                  </p>
                </div>

                <div className="mb-6 text-gray-800 bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                  <p className="mb-2 italic text-gray-600">
                    Thanking you in advance for your cooperation,
                  </p>
                  <div className="mt-4">
                    <p className="font-bold text-gray-900 text-lg">
                      {seniorStaffNurse}
                    </p>
                    <p className="text-blue-600 font-medium">Director of Nursing</p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t-2 border-gray-200">
                  <p className="font-bold mb-4 text-gray-800">
                    Acknowledgement of receipt of "PATIENT RECORDS - STATE MANDATE
                    COMPLIANCE" Notice and AGREEMENT TO COMPLY with the above
                    mentioned State and Agency Regulations.
                  </p>

                  <div className="mb-6">
                    <EvaluationInput
                      name="clinicianName"
                      label="Clinician Name"
                      placeholder="Dr. Jane Smith"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                      }}
                    />
                  </div>
                </div>
              </fieldset>
              <ApplicationFormFooter
                page={1}
                setPage={() => { }}
                totalPages={1}
                methods={methods}
                onSave={handleSave}
                onSubmit={(data, status) => onSubmit(data, status)}
                onStatusUpdate={handleStatusAction}
                isSignatureModalOpen={isSignatureModalOpen}
                openSignatureModal={openSignatureModal}
                closeSignatureModal={closeSignatureModal}
                handleSignatureApply={handleSignatureApply}
                signerName={signerName}
                requireSignatures={true}
                showSignatureSections={true}
                applicantName={watch("applicantName") || ""}
                applicantSignature={watch("applicantSignature") || ""}
                applicantSignedAt={watch("applicantSignedAt") || ""}
                reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                reviewedBySignature={watch("reviewedBySignature") || ""}
                reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                submitLabel="Submit & Print"
                isReadOnly={isReadOnly}
                onClose={closeForm}
                currentStatus={currentStatus}
              />
            </form>


          </FormProvider>
        </div>
      </div>
    </>
  );
};

export default StateMandateCompliance;
